import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "./firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import "./EditQuestion.css";

const EditQuestion = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [question, setQuestion] = useState({
    questionText: "",
    markSchemeAnswer: "",
    maxMark: "",
    topic: "",
    subject: "",
    level: "",
    specCode: "",
    textBoxSize: "medium",
  });

  const [questionImage, setQuestionImage] = useState(null);
  const [markSchemeImage, setMarkSchemeImage] = useState(null);
  const [questionImageURL, setQuestionImageURL] = useState("");
  const [markSchemeImageURL, setMarkSchemeImageURL] = useState("");
  const [originalQuestionImageURL, setOriginalQuestionImageURL] = useState("");
  const [originalMarkSchemeImageURL, setOriginalMarkSchemeImageURL] = useState("");

  // Fetch question data on component mount
  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        console.log("Fetching question with ID:", questionId);
        const docRef = doc(db, "questions", questionId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Question data loaded:", data);
          
          setQuestion({
            questionText: data.questionText || "",
            markSchemeAnswer: data.markSchemeAnswer || "",
            maxMark: data.maxMark || "",
            topic: data.topic || "",
            subject: data.subject || "",
            level: data.level || "",
            specCode: data.specCode || "",
            textBoxSize: data.textBoxSize || "medium",
          });
          
          // Set image URLs if they exist
          if (data.questionImageURL) {
            setQuestionImageURL(data.questionImageURL);
            setOriginalQuestionImageURL(data.questionImageURL);
          }
          
          if (data.markSchemeImageURL) {
            setMarkSchemeImageURL(data.markSchemeImageURL);
            setOriginalMarkSchemeImageURL(data.markSchemeImageURL);
          }
        } else {
          console.log("No such document!");
          showToast("Question not found", "error");
          navigate(-1); // Go back to previous page
        }
      } catch (error) {
        console.error("Error fetching question: ", error.message);
        showToast("Error loading question", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId, navigate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuestion((prev) => ({ ...prev, [name]: value }));
  };

  // Handle file selection for question image
  const handleQuestionFileChange = (e) => {
    if (e.target.files[0]) {
      setQuestionImage(e.target.files[0]);
    }
  };

  // Handle file selection for mark scheme image
  const handleMarkSchemeFileChange = (e) => {
    if (e.target.files[0]) {
      setMarkSchemeImage(e.target.files[0]);
    }
  };

  // Upload question image
  const uploadQuestionImage = async () => {
    if (!questionImage) return;
    
    try {
      const storageRef = ref(
        storage,
        `questions/${Date.now()}_${questionImage.name}`
      );
      await uploadBytes(storageRef, questionImage);
      const url = await getDownloadURL(storageRef);
      setQuestionImageURL(url);
      showToast("Question image uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading question image:", error.message);
      showToast("Error uploading question image", "error");
    }
  };

  // Upload mark scheme image
  const uploadMarkSchemeImage = async () => {
    if (!markSchemeImage) return;
    
    try {
      const storageRef = ref(
        storage,
        `markschemes/${Date.now()}_${markSchemeImage.name}`
      );
      await uploadBytes(storageRef, markSchemeImage);
      const url = await getDownloadURL(storageRef);
      setMarkSchemeImageURL(url);
      showToast("Mark scheme image uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading mark scheme image:", error.message);
      showToast("Error uploading mark scheme image", "error");
    }
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `toast ${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    // Validate required fields
    if (
      !question.maxMark ||
      !question.topic ||
      !question.subject ||
      !question.level ||
      !question.specCode
    ) {
      showToast("Max Mark, Topic, Subject, Level, and Spec Code are required!", "error");
      setIsSaving(false);
      return;
    }

    // For the question field, require either text OR an uploaded image.
    if (!question.questionText && !questionImageURL && !questionImage) {
      showToast("Please provide either a question text or upload a question image.", "error");
      setIsSaving(false);
      return;
    }

    // For the mark scheme field, require either text OR an uploaded image.
    if (!question.markSchemeAnswer && !markSchemeImageURL && !markSchemeImage) {
      showToast("Please provide either a mark scheme text or upload a mark scheme image.", "error");
      setIsSaving(false);
      return;
    }

    try {
      // Create a copy of the question data for the update
      const questionData = {
        ...question,
        maxMark: parseInt(question.maxMark, 10),
        updatedAt: Timestamp.now(),
      };
      
      // Handle question image - if there's a new image selected but not uploaded yet
      if (questionImage) {
        try {
          // Upload the new question image
          const storageRef = ref(
            storage,
            `questions/${Date.now()}_${questionImage.name}`
          );
          await uploadBytes(storageRef, questionImage);
          const url = await getDownloadURL(storageRef);
          questionData.questionImageURL = url;
          // Update the state for future reference
          setQuestionImageURL(url);
          setOriginalQuestionImageURL(url);
          showToast("Question image uploaded successfully", "success");
        } catch (error) {
          console.error("Error uploading question image:", error.message);
          showToast("Error uploading question image", "error");
          setIsSaving(false);
          return;
        }
      } else if (questionImageURL) {
        // Use the already uploaded image URL
        questionData.questionImageURL = questionImageURL;
      } else if (originalQuestionImageURL) {
        // Keep the original image URL if no new image was uploaded
        questionData.questionImageURL = originalQuestionImageURL;
      }
      
      // Handle mark scheme image - if there's a new image selected but not uploaded yet
      if (markSchemeImage) {
        try {
          // Upload the new mark scheme image
          const storageRef = ref(
            storage,
            `markschemes/${Date.now()}_${markSchemeImage.name}`
          );
          await uploadBytes(storageRef, markSchemeImage);
          const url = await getDownloadURL(storageRef);
          questionData.markSchemeImageURL = url;
          // Update the state for future reference
          setMarkSchemeImageURL(url);
          setOriginalMarkSchemeImageURL(url);
          showToast("Mark scheme image uploaded successfully", "success");
        } catch (error) {
          console.error("Error uploading mark scheme image:", error.message);
          showToast("Error uploading mark scheme image", "error");
          setIsSaving(false);
          return;
        }
      } else if (markSchemeImageURL) {
        // Use the already uploaded image URL
        questionData.markSchemeImageURL = markSchemeImageURL;
      } else if (originalMarkSchemeImageURL) {
        // Keep the original image URL if no new image was uploaded
        questionData.markSchemeImageURL = originalMarkSchemeImageURL;
      }
      
      console.log("Updating question with data:", questionData);
      
      // Update the document in Firestore
      const questionRef = doc(db, "questions", questionId);
      await updateDoc(questionRef, questionData);
      
      console.log("Update successful");
      showToast("Question updated successfully!", "success");
      
      // Navigate back to the question manager
      setTimeout(() => {
        navigate(-1); // Go back to previous page
      }, 1500);
    } catch (error) {
      console.error("Error updating question: ", error.message);
      showToast("Error updating question. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading question...</p>
      </div>
    );
  }

  return (
    <div className="edit-question-container">
      {/* Header */}
      <div className="header">
        <h1>Edit Question</h1>
      </div>

      {/* Action Buttons Row */}
      <div className="action-button-row">
        {/* Back Button */}
        <button
          className="back-button"
          onClick={() => navigate(-1)} // Go back to previous page
        >
          ← Back to Question Manager
        </button>
      </div>

      {/* Edit Form */}
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div>
              <label className="form-label">
                Question Text
              </label>
              <textarea
                name="questionText"
                placeholder="Enter question text here (Optional if image uploaded)"
                value={question.questionText}
                onChange={handleInputChange}
                className="textarea-field"
              />
            </div>

            <div>
              <label className="form-label">
                Mark Scheme Answer
              </label>
              <textarea
                name="markSchemeAnswer"
                placeholder="Enter mark scheme here (Optional if image uploaded)"
                value={question.markSchemeAnswer}
                onChange={handleInputChange}
                className="textarea-field"
              />
            </div>
          </div>

          {/* Image Upload Row */}
          <div className="image-upload-container">
            {/* Question Image Upload */}
            <div>
              <label className="form-label">
                Question Image (Optional)
              </label>
              {originalQuestionImageURL && (
                <div className="current-image-container">
                  <p className="current-image-label">Current Image:</p>
                  <img
                    src={originalQuestionImageURL}
                    alt="Current Question"
                    className="current-image"
                  />
                </div>
              )}
              <div className="upload-button-container">
                <label
                  htmlFor="questionImageInput"
                  className="file-browser-button"
                >
                  Browse Files
                </label>
                <span className="file-name">
                  {questionImage ? questionImage.name : "No new file selected"}
                </span>
              </div>
              <input
                id="questionImageInput"
                type="file"
                accept="image/*"
                onChange={handleQuestionFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={uploadQuestionImage}
                disabled={!questionImage}
                className="file-browser-button"
                style={{
                  marginTop: "8px",
                  opacity: questionImage ? 1 : 0.6,
                  cursor: questionImage ? "pointer" : "not-allowed",
                }}
              >
                Upload New Question Image
              </button>
              {questionImage && questionImageURL && (
                <div style={{ marginTop: "8px" }}>
                  <p className="new-image-label">New Image Preview:</p>
                  <img
                    src={questionImageURL}
                    alt="New Question Preview"
                    className="image-preview"
                  />
                </div>
              )}
            </div>

            {/* Mark Scheme Image Upload */}
            <div>
              <label className="form-label">
                Mark Scheme Image (Optional)
              </label>
              {originalMarkSchemeImageURL && (
                <div className="current-image-container">
                  <p className="current-image-label">Current Image:</p>
                  <img
                    src={originalMarkSchemeImageURL}
                    alt="Current Mark Scheme"
                    className="current-image"
                  />
                </div>
              )}
              <div className="upload-button-container">
                <label
                  htmlFor="markSchemeImageInput"
                  className="file-browser-button"
                >
                  Browse Files
                </label>
                <span className="file-name">
                  {markSchemeImage ? markSchemeImage.name : "No new file selected"}
                </span>
              </div>
              <input
                id="markSchemeImageInput"
                type="file"
                accept="image/*"
                onChange={handleMarkSchemeFileChange}
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={uploadMarkSchemeImage}
                disabled={!markSchemeImage}
                className="file-browser-button"
                style={{
                  marginTop: "8px",
                  opacity: markSchemeImage ? 1 : 0.6,
                  cursor: markSchemeImage ? "pointer" : "not-allowed",
                }}
              >
                Upload New Mark Scheme Image
              </button>
              {markSchemeImage && markSchemeImageURL && (
                <div style={{ marginTop: "8px" }}>
                  <p className="new-image-label">New Image Preview:</p>
                  <img
                    src={markSchemeImageURL}
                    alt="New Mark Scheme Preview"
                    className="image-preview"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Additional Fields Row */}
          <div className="form-fields-container">
            <div>
              <label className="form-label">
                Max Mark *
              </label>
              <input
                type="number"
                name="maxMark"
                placeholder="e.g. 5"
                value={question.maxMark}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Topic *
              </label>
              <input
                type="text"
                name="topic"
                placeholder="e.g. Photosynthesis"
                value={question.topic}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                placeholder="e.g. Biology"
                value={question.subject}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Level *
              </label>
              <input
                type="text"
                name="level"
                placeholder="e.g. GCSE"
                value={question.level}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Spec Code *
              </label>
              <input
                type="text"
                name="specCode"
                placeholder="e.g. AQA-BIO-4.1"
                value={question.specCode}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Answer Input Size
              </label>
              <select
                name="textBoxSize"
                value={question.textBoxSize}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
          </div>

          <div className="form-submit-container">
            <button
              type="submit"
              disabled={isSaving}
              className="submit-button"
              style={{ opacity: isSaving ? 0.7 : 1 }}
            >
              {isSaving ? "Saving..." : "Update Question"}
            </button>
            <span className="required-field-note">
              * Required fields
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditQuestion;
